/**
 * V2 Roller Style Configuration
 *
 * Factory for style section configs adapted to V2 roller schema paths.
 *
 * Adapted from V1 picker style fields:
 * - V1: picker.labelStyles.* -> V2: content.roller.labelAppearance.*
 * - V1: picker.styles.backgroundColor -> V2: content.roller.appearance.backgroundColor
 * - V1: picker.styles.borderColor -> V2: content.roller.appearance.borders.color
 * - V1: picker.styles.borderWidth -> V2: content.roller.appearance.borders.width
 * - V1: picker.styles.borderRadius -> V2: content.roller.appearance.borders.radius.uniform
 * - V1: picker.styles.paddingTop -> V2: content.roller.appearance.insets.top
 *
 * Sprint: schema-v2-migration, Sprint 4 fix
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import type { ListStyleSectionConfig } from '../types/listSection.types';
import {
  NUMBER_0_50_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  TEXT_ALIGN_OPTIONS,
} from './atomicFields';

// ============================================
// V2 Roller Label Style Fields
// ============================================

/**
 * Create V2 label text style fields for roller
 * Controls the display of the selected value text
 */
function createV2RollerLabelFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.fontSize`,
      label: 'font size',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 18,
      defaultScrollValue: 18,
      isNullable: true,
      guideKey: 'screenedit-roller-label-font-size',
    },
    {
      path: `${basePath}.fontWeight`,
      label: 'font weight',
      type: 'select',
      options: [...FONT_WEIGHT_OPTIONS_SHORT],
      defaultValue: 400,
      isNullable: true,
      numericValue: true,
      guideKey: 'screenedit-roller-label-font-weight',
    },
    {
      path: `${basePath}.color`,
      label: 'text color',
      type: 'color',
      defaultValue: '#000000',
      isNullable: true,
      guideKey: 'screenedit-roller-label-text-color',
    },
    {
      path: `${basePath}.align`,
      label: 'text align',
      type: 'select',
      options: [...TEXT_ALIGN_OPTIONS],
      defaultValue: 'center',
      isNullable: true,
      guideKey: 'screenedit-roller-label-text-align',
    },
  ];
}

// ============================================
// V2 Roller Container Style Fields
// ============================================

/**
 * Create V2 roller container style fields
 * Controls background, borders of the picker container
 */
function createV2RollerContainerFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.backgroundColor`,
      label: 'background color',
      type: 'color',
      defaultValue: '#ffffff',
      isNullable: true,
      guideKey: 'screenedit-roller-container-background-color',
    },
    {
      path: `${basePath}.borders.width`,
      label: 'border width',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 1,
      defaultScrollValue: 1,
      isNullable: true,
      guideKey: 'screenedit-roller-border-width',
    },
    {
      path: `${basePath}.borders.color`,
      label: 'border color',
      type: 'color',
      defaultValue: '#E0E0E0',
      isNullable: true,
      guideKey: 'screenedit-roller-border-color',
    },
    {
      path: `${basePath}.borders.radius.uniform`,
      label: 'border radius',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 12,
      defaultScrollValue: 12,
      isNullable: true,
      guideKey: 'screenedit-roller-border-radius',
    },
  ];
}

// ============================================
// Main Factory
// ============================================

/**
 * Create V2 roller style sections
 *
 * Returns 2 collapsible sections matching V1 picker style editors:
 * 1. Label styles (fontSize, fontWeight, color, align)
 * 2. Container styles (backgroundColor, borders + padding)
 */
export function createV2RollerStyleSections(): ListStyleSectionConfig[] {
  const rollerBase = 'content.roller';

  return [
    // Label text styles
    {
      id: 'labelStyles',
      title: 'Label styles',
      defaultExpanded: false,
      fields: createV2RollerLabelFields(`${rollerBase}.labelAppearance`),
    },
    // Container styles
    {
      id: 'containerStyles',
      title: 'Container styles',
      defaultExpanded: false,
      fields: createV2RollerContainerFields(`${rollerBase}.appearance`),
      paddingConfig: {
        basePath: `${rollerBase}.appearance.insets`,
        label: 'Container padding',
      },
    },
  ];
}
